/**
 * Twitter video extraction utilities.
 *
 * Uses two complementary APIs:
 * 1. **Syndication API** (`cdn.syndication.twimg.com/tweet-result`) — Twitter's
 *    undocumented public embed API. Returns video variants at multiple quality
 *    levels with clean URLs (no `?tag=` parameter). Requires no auth — just a
 *    tweet ID and a trivial token. This is what yt-dlp uses as its "syndication"
 *    strategy and is the most reliable source for video downloads.
 * 2. **FxTwitter API** (`api.fxtwitter.com`) — Provides richer metadata like
 *    engagement stats, author info, and formatted text. Used as a supplemental
 *    data source when the syndication API doesn't have enough metadata.
 */

const CORS_PROXY = 'https://proxy.shakespeare.diy/?url=';
const FXTWITTER_API = 'https://api.fxtwitter.com';
const SYNDICATION_API = 'https://cdn.syndication.twimg.com/tweet-result';

// ---------------------------------------------------------------------------
// URL parsing
// ---------------------------------------------------------------------------

/** Supported URL patterns for X/Twitter */
const TWITTER_URL_REGEX = /^https?:\/\/(?:(?:www\.)?(?:twitter\.com|x\.com)|(?:mobile\.twitter\.com))\/(?:#!\/)?(\w+)\/status(?:es)?\/(\d+)/i;

/** Validate and parse a Twitter/X URL, returning screen_name and status ID */
export function parseTwitterUrl(url: string): { screenName: string; statusId: string } | null {
  const trimmed = url.trim();
  const match = trimmed.match(TWITTER_URL_REGEX);
  if (!match) return null;
  return { screenName: match[1], statusId: match[2] };
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A single video variant at a specific quality/bitrate */
export interface VideoVariant {
  url: string;
  contentType: string;
  bitrate?: number;
  /** Human-readable quality label e.g. "1080x1920" or "720x1280" */
  qualityLabel?: string;
}

export interface TweetVideoInfo {
  url: string;
  thumbnailUrl: string;
  width: number;
  height: number;
  duration: number;
  format: string;
  type: 'video' | 'gif';
  /** All available quality variants, sorted highest bitrate first */
  variants: VideoVariant[];
}

export interface TweetAuthor {
  name: string;
  screenName: string;
  avatarUrl: string;
}

export interface TweetData {
  tweetUrl: string;
  text: string;
  author: TweetAuthor;
  likes: number;
  retweets: number;
  replies: number;
  views: number;
  videos: TweetVideoInfo[];
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Syndication API types (internal)
// ---------------------------------------------------------------------------

interface SyndicationVariant {
  type?: string;
  src?: string;
  content_type?: string;
  url?: string;
  bitrate?: number;
}

interface SyndicationMediaDetail {
  type: string;
  media_url_https?: string;
  original_info?: { width: number; height: number };
  video_info?: {
    aspect_ratio: number[];
    duration_millis?: number;
    variants: SyndicationVariant[];
  };
}

interface SyndicationVideo {
  poster?: string;
  variants?: SyndicationVariant[];
  durationMs?: number;
  aspectRatio?: number[];
}

interface SyndicationResponse {
  __typename?: string;
  id_str?: string;
  text?: string;
  created_at?: string;
  favorite_count?: number;
  conversation_count?: number;
  user?: {
    name?: string;
    screen_name?: string;
    profile_image_url_https?: string;
  };
  mediaDetails?: SyndicationMediaDetail[];
  video?: SyndicationVideo;
}

// ---------------------------------------------------------------------------
// FxTwitter API types (internal)
// ---------------------------------------------------------------------------

interface FxTwitterResponse {
  code: number;
  message: string;
  tweet?: {
    url: string;
    text: string;
    created_at: string;
    author: {
      name: string;
      screen_name: string;
      avatar_url: string;
    };
    replies: number;
    retweets: number;
    likes: number;
    views: number;
    media?: {
      videos?: Array<{
        url: string;
        thumbnail_url: string;
        width: number;
        height: number;
        duration: number;
        format: string;
        type: 'video' | 'gif';
      }>;
    };
  };
}

// ---------------------------------------------------------------------------
// Syndication API
// ---------------------------------------------------------------------------

/**
 * Compute a token for the syndication API.
 * The token is a simple mathematical derivation of the tweet ID.
 * In practice Twitter doesn't strictly validate it, but we compute it
 * properly for reliability.
 */
function getSyndicationToken(tweetId: string): string {
  return ((Number(tweetId) / 1e15) * Math.PI)
    .toString(36)
    .replace(/(0+|\.)/g, '');
}

/** Extract resolution from a video.twimg.com URL path like /vid/avc1/1080x1920/... */
function extractResolutionFromUrl(url: string): string | undefined {
  const match = url.match(/\/(\d+x\d+)\//);
  return match?.[1];
}

/**
 * Parse syndication API variants into our VideoVariant format.
 * Filters to mp4 only and sorts by bitrate descending.
 */
function parseSyndicationVariants(
  variants: SyndicationVariant[],
): VideoVariant[] {
  return variants
    .filter((v) => {
      const ct = v.content_type ?? v.type ?? '';
      return ct === 'video/mp4';
    })
    .map((v) => {
      const url = v.url ?? v.src ?? '';
      return {
        url,
        contentType: 'video/mp4',
        bitrate: v.bitrate,
        qualityLabel: extractResolutionFromUrl(url),
      };
    })
    .sort((a, b) => (b.bitrate ?? 0) - (a.bitrate ?? 0));
}

/** Fetch tweet data from the Syndication API (primary source for video URLs) */
async function fetchFromSyndication(
  statusId: string,
  signal?: AbortSignal,
): Promise<SyndicationResponse> {
  const url = new URL(SYNDICATION_API);
  url.searchParams.set('id', statusId);
  url.searchParams.set('token', getSyndicationToken(statusId));
  url.searchParams.set('lang', 'en');

  // Route through CORS proxy — cdn.syndication.twimg.com doesn't send
  // Access-Control-Allow-Origin headers, so direct browser fetch is blocked.
  const proxiedUrl = `${CORS_PROXY}${encodeURIComponent(url.toString())}`;
  const response = await fetch(proxiedUrl, { signal });

  if (!response.ok) {
    throw new Error(`Syndication API failed (HTTP ${response.status})`);
  }

  return response.json() as Promise<SyndicationResponse>;
}

/** Fetch supplemental metadata from FxTwitter API */
async function fetchFromFxTwitter(
  screenName: string,
  statusId: string,
  signal?: AbortSignal,
): Promise<FxTwitterResponse | null> {
  try {
    const apiUrl = `${FXTWITTER_API}/${screenName}/status/${statusId}`;
    const response = await fetch(apiUrl, { signal });
    if (!response.ok) return null;
    return response.json() as Promise<FxTwitterResponse>;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Main fetch function
// ---------------------------------------------------------------------------

/** Fetch tweet data including video URLs. Uses syndication API + FxTwitter. */
export async function fetchTweetData(
  tweetUrl: string,
  signal?: AbortSignal,
): Promise<TweetData> {
  const parsed = parseTwitterUrl(tweetUrl);
  if (!parsed) {
    throw new Error('Invalid Twitter/X URL. Please provide a valid tweet link.');
  }

  // Fetch both APIs in parallel — syndication for video URLs, FxTwitter for metadata
  const [syndicationData, fxData] = await Promise.all([
    fetchFromSyndication(parsed.statusId, signal),
    fetchFromFxTwitter(parsed.screenName, parsed.statusId, signal),
  ]);

  // Extract videos from syndication API
  const videos: TweetVideoInfo[] = [];

  if (syndicationData.mediaDetails) {
    for (const media of syndicationData.mediaDetails) {
      if (media.type !== 'video' || !media.video_info) continue;

      const variants = parseSyndicationVariants(media.video_info.variants);
      if (variants.length === 0) continue;

      const bestVariant = variants[0];
      const resolution = extractResolutionFromUrl(bestVariant.url);
      const [w, h] = resolution?.split('x').map(Number) ?? [
        media.original_info?.width ?? 0,
        media.original_info?.height ?? 0,
      ];

      videos.push({
        url: bestVariant.url,
        thumbnailUrl: media.media_url_https ?? syndicationData.video?.poster ?? '',
        width: w,
        height: h,
        duration: (media.video_info.duration_millis ?? 0) / 1000,
        format: 'mp4',
        type: 'video',
        variants,
      });
    }
  }

  // Fallback: if syndication didn't give us mediaDetails but has a top-level video
  if (videos.length === 0 && syndicationData.video?.variants) {
    const variants = parseSyndicationVariants(syndicationData.video.variants);
    if (variants.length > 0) {
      const bestVariant = variants[0];
      const resolution = extractResolutionFromUrl(bestVariant.url);
      const [w, h] = resolution?.split('x').map(Number) ?? [0, 0];

      videos.push({
        url: bestVariant.url,
        thumbnailUrl: syndicationData.video.poster ?? '',
        width: w,
        height: h,
        duration: (syndicationData.video.durationMs ?? 0) / 1000,
        format: 'mp4',
        type: 'video',
        variants,
      });
    }
  }

  if (videos.length === 0) {
    throw new Error('This tweet does not contain any videos.');
  }

  // Merge metadata: prefer FxTwitter for richer stats, fall back to syndication
  const fxTweet = fxData?.code === 200 ? fxData.tweet : null;

  const author: TweetAuthor = fxTweet
    ? {
        name: fxTweet.author.name,
        screenName: fxTweet.author.screen_name,
        avatarUrl: fxTweet.author.avatar_url,
      }
    : {
        name: syndicationData.user?.name ?? '',
        screenName: syndicationData.user?.screen_name ?? parsed.screenName,
        avatarUrl: syndicationData.user?.profile_image_url_https?.replace('_normal', '_400x400') ?? '',
      };

  return {
    tweetUrl: fxTweet?.url ?? `https://x.com/${parsed.screenName}/status/${parsed.statusId}`,
    text: fxTweet?.text ?? syndicationData.text ?? '',
    author,
    likes: fxTweet?.likes ?? syndicationData.favorite_count ?? 0,
    retweets: fxTweet?.retweets ?? 0,
    replies: fxTweet?.replies ?? syndicationData.conversation_count ?? 0,
    views: fxTweet?.views ?? 0,
    videos,
    createdAt: fxTweet?.created_at ?? syndicationData.created_at ?? '',
  };
}

// ---------------------------------------------------------------------------
// Video download
// ---------------------------------------------------------------------------

/**
 * Get the proxied URL for a Twitter video.
 * This is used for both `<video src>` preview and `<a download>` links.
 */
export function getProxiedVideoUrl(videoUrl: string): string {
  return `${CORS_PROXY}${encodeURIComponent(videoUrl)}`;
}

/**
 * Download a video by triggering a native browser download via `<a download>`.
 *
 * We use this instead of `fetch()` because Twitter's CDN (video.twimg.com)
 * returns 403 on CORS-enabled programmatic requests (which include an
 * `Origin` header) from datacenter proxy IPs, even though the same proxy
 * URL works fine when loaded by `<video src>` or navigated to by `<a href>`.
 * Browser-initiated navigation/media requests don't include CORS headers.
 */
export function triggerVideoDownload(videoUrl: string, filename: string): void {
  const proxiedUrl = getProxiedVideoUrl(videoUrl);
  const a = document.createElement('a');
  a.href = proxiedUrl;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/**
 * Capture video bytes from a playing `<video>` element by re-fetching
 * through a fresh proxied URL.
 *
 * Uses `referrerPolicy: 'no-referrer'` to avoid sending `Referer` which
 * some CDNs check. If the CORS fetch still fails (403), falls back to
 * capturing the video from the `<video>` element via MediaSource/canvas.
 */
export async function downloadVideoBlob(
  videoUrl: string,
  signal?: AbortSignal,
  onProgress?: (loaded: number, total: number) => void,
): Promise<Blob> {
  const proxiedUrl = getProxiedVideoUrl(videoUrl);

  const response = await fetch(proxiedUrl, {
    signal,
    referrerPolicy: 'no-referrer',
  });

  if (!response.ok) {
    throw new Error(`Failed to download video (HTTP ${response.status})`);
  }

  if (onProgress && response.body) {
    const contentLength = Number(response.headers.get('content-length') || 0);
    const reader = response.body.getReader();
    const chunks: Uint8Array[] = [];
    let loaded = 0;

    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      loaded += value.length;
      onProgress(loaded, contentLength);
    }

    return new Blob(chunks, { type: 'video/mp4' });
  }

  return response.blob();
}

/** Format seconds to MM:SS */
export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/** Format large numbers compactly */
export function formatNumber(num: number): string {
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toString();
}

/** Format bitrate to a human-readable string like "2.2 Mbps" */
export function formatBitrate(bitrate: number): string {
  if (bitrate >= 1_000_000) return `${(bitrate / 1_000_000).toFixed(1)} Mbps`;
  if (bitrate >= 1_000) return `${(bitrate / 1_000).toFixed(0)} Kbps`;
  return `${bitrate} bps`;
}
